import React, { FC, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import {
  FormController,
  MuiBox,
  MuiSelect,
  MuiTable,
  MuiTextFieldWithSearchIcon,
} from "../../../../../components";
import { MuiNotesToolTip } from "../../../../../components/tooltip/notesToolTip/MuiNotesToolTip";
import { MAX_LENGTH_75 } from "../../../../../helpers/constants";
import { LabelValue } from "../../../../../helpers/types/common";
import {
  checkFilterMatch,
  getValueOrBlank,
  isNotEmpty,
} from "../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import {
  CompleteTransferParticipantResult,
  CompleteTransferParticipantResultFormKey,
  transferTypeKey,
} from "../../../modelsAndSchemas/Models";
import { bulkTransferSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";
import { SearchParticipantsResultWrapper } from "../bulkTransferSearchParticipants/searchParticipantsResult/SearchParticipantsResult.style";

interface SearchParticipantsResultProps {
  rows: CompleteTransferParticipantResult[];
  pageId: string;
  transferType: string;
  isUnprocessed?: boolean;
}

export const CompleteBulkTransferResult: FC<SearchParticipantsResultProps> = ({
  rows,
  pageId,
  transferType,
  isUnprocessed,
}) => {
  const { fetchLabel, commonLabels } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const tableRef = React.useRef<{ resetPagination: () => void }>(null);

  const [filteredResults, setFilteredResults] = useState([...rows]);

  const [allUpdatedRows, setAllUpdatedRows] = useState<
    CompleteTransferParticipantResult[]
  >([]);
  const [uniqueLineOfBusinesses, setUniqueLineOfBusinesses] = useState<
    LabelValue[]
  >([]);
  const [uniqueSourceProgramTemplates, setUniqueSourceProgramTemplates] =
    useState<LabelValue[]>([]);
  const [
    uniqueCompatibleProgramTemplates,
    setUniqueCompatibleProgramTemplates,
  ] = useState<LabelValue[]>([]);
  const [uniqueSourceCountry, setUniqueSourceCountry] = useState<LabelValue[]>(
    [],
  );
  const [uniqueDestinationCountry, setUniqueDestinationCountry] = useState<
    LabelValue[]
  >([]);

  const [uniqueNextView, setUniqueNextView] = useState<LabelValue[]>([]);

  const getTransferLabel = (key: string) => {
    return fetchLabel(bulkTransferSiteCoreKeys.bulkTransferSummary, key);
  };
  const getResultLabel = (key: string) => {
    return fetchLabel(
      bulkTransferSiteCoreKeys.searchParticipantsResultsKey,
      key,
    );
  };

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  const defaultSearchParticipantResult = {
    [CompleteTransferParticipantResultFormKey.participantName]: "",
    [CompleteTransferParticipantResultFormKey.sourceCountryName]: "",
    [CompleteTransferParticipantResultFormKey.destinationCountryName]: "",
    [CompleteTransferParticipantResultFormKey.productLine]: "",
    [CompleteTransferParticipantResultFormKey.sourceProgramTemplate]: "",
    [CompleteTransferParticipantResultFormKey.compatibleProgramTemplate]: "",
    [CompleteTransferParticipantResultFormKey.clientName]: "",
    [CompleteTransferParticipantResultFormKey.nextViewName]: "",
    [CompleteTransferParticipantResultFormKey.notes]: "",
  };

  const methods = useForm({
    mode: "all",
    defaultValues: defaultSearchParticipantResult,
  });

  const { reset, control, getValues } = methods;

  const filters = useWatch({ control });

  useEffect(() => {
    resetPagination();
    const myData = structuredClone(rows);

    const uniqueSourceCountries = Array.from(
      new Set(myData.map((item) => item.sourceCountryName)),
    ).map((country) => ({ value: country, label: country }));

    const uniqueDestinationCountries = Array.from(
      new Set(myData.map((item) => item.destinationCountryName)),
    ).map((country) => ({ value: country, label: country }));

    const uniqueSourceProgramTemplate = Array.from(
      new Set(myData.map((item) => item.sourceProgramTemplateName)),
    ).map((label) => ({
      value: label,
      label: label,
    }));
    const uniqueCompatibleProgramTemplate = Array.from(
      new Set(myData.map((item) => item.compatibleProgramTemplateName)),
    ).map((label) => ({
      value: label,
      label: label,
    }));

    const uniqueLineOfBusiness = Array.from(
      new Set(myData.map((item) => item.productLineName)),
    ).map((label) => ({
      value: label,
      label: label,
    }));
    const uniqueNextViews = Array.from(
      new Set(myData.map((item) => item.nextViewName)),
    ).map((label) => ({
      value: label,
      label: label,
    }));

    setUniqueLineOfBusinesses(uniqueLineOfBusiness);
    setUniqueSourceCountry(uniqueSourceCountries);
    setUniqueSourceProgramTemplates(uniqueSourceProgramTemplate);
    setUniqueDestinationCountry(uniqueDestinationCountries);
    setUniqueCompatibleProgramTemplates(uniqueCompatibleProgramTemplate);
    setUniqueNextView(uniqueNextViews);
    setAllUpdatedRows(myData);
    setFilteredResults([...myData]);
    reset();
  }, [rows]);

  useEffect(() => {
    if (isNotEmpty(allUpdatedRows)) {
      const {
        participantName,
        sourceCountryName,
        destinationCountryName,
        productLineName,
        sourceProgramTemplateName,
        compatibleProgramTemplateName,
        clientName,
        nextViewName,
      } = getValues();

      const myData = allUpdatedRows.filter(
        (item: CompleteTransferParticipantResult) => {
          const participantNameMatch = checkFilterMatch(
            participantName,
            getValueOrBlank(item.participantName),
          );

          const sourceCountryNameMatch = checkFilterMatch(
            sourceCountryName,
            item.sourceCountryName,
          );
          const destinationCountryNameMatch = checkFilterMatch(
            destinationCountryName,
            item.destinationCountryName,
          );

          const lineOfBusinessMatch = checkFilterMatch(
            productLineName,
            item.productLineName,
          );

          const nextViewMatch = checkFilterMatch(
            nextViewName,
            item.nextViewName,
          );

          const sourceProgramTemplateNameMatch = checkFilterMatch(
            sourceProgramTemplateName,
            item.sourceProgramTemplateName,
          );
          const compatibleProgramTemplateNameMatch = checkFilterMatch(
            compatibleProgramTemplateName,
            item.compatibleProgramTemplateName,
          );

          const clientNameMatch = checkFilterMatch(clientName, item.clientName);

          return (
            participantNameMatch &&
            lineOfBusinessMatch &&
            sourceCountryNameMatch &&
            destinationCountryNameMatch &&
            nextViewMatch &&
            clientNameMatch &&
            sourceProgramTemplateNameMatch &&
            compatibleProgramTemplateNameMatch
          );
        },
      );
      setFilteredResults([...myData]);
      resetPagination();
    }
  }, [filters]);

  const countryTransferSequence = [
    CompleteTransferParticipantResultFormKey.participantName,
    CompleteTransferParticipantResultFormKey.sourceCountryName,
    CompleteTransferParticipantResultFormKey.destinationCountryName,
    CompleteTransferParticipantResultFormKey.productLine,
    CompleteTransferParticipantResultFormKey.sourceProgramTemplate,
    CompleteTransferParticipantResultFormKey.compatibleProgramTemplate,
    CompleteTransferParticipantResultFormKey.clientName,
    CompleteTransferParticipantResultFormKey.nextViewName,
  ];

  const programTransferSequence = [
    CompleteTransferParticipantResultFormKey.participantName,
    CompleteTransferParticipantResultFormKey.productLine,
    CompleteTransferParticipantResultFormKey.sourceProgramTemplate,
    CompleteTransferParticipantResultFormKey.compatibleProgramTemplate,
    CompleteTransferParticipantResultFormKey.nextViewName,
    CompleteTransferParticipantResultFormKey.clientName,
    CompleteTransferParticipantResultFormKey.sourceCountryName,
    CompleteTransferParticipantResultFormKey.destinationCountryName,
  ];

  const fullColumnMap = {
    [CompleteTransferParticipantResultFormKey.participantName]: {
      id: CompleteTransferParticipantResultFormKey.participantName,
      label: getResultLabel(bulkTransferSiteCoreKeys.participantName),
      isSortVisible: true,
      isEmptyValue: true,
    },
    [CompleteTransferParticipantResultFormKey.sourceCountryName]: {
      id: CompleteTransferParticipantResultFormKey.sourceCountryName,
      label: getTransferLabel(bulkTransferSiteCoreKeys.sourceCountry),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
    },
    [CompleteTransferParticipantResultFormKey.destinationCountryName]: {
      id: CompleteTransferParticipantResultFormKey.destinationCountryName,
      label: getTransferLabel(
        bulkTransferSiteCoreKeys.destinationCountrySummaryTransfer,
      ),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
    },
    [CompleteTransferParticipantResultFormKey.productLine]: {
      id: CompleteTransferParticipantResultFormKey.productLine,
      label: getResultLabel(bulkTransferSiteCoreKeys.resultProductLine),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
    },
    [CompleteTransferParticipantResultFormKey.sourceProgramTemplate]: {
      id: CompleteTransferParticipantResultFormKey.sourceProgramTemplate,
      label: getTransferLabel(bulkTransferSiteCoreKeys.sourceProgramTemplate),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
    },
    [CompleteTransferParticipantResultFormKey.compatibleProgramTemplate]: {
      id: CompleteTransferParticipantResultFormKey.compatibleProgramTemplate,
      label: "Compatible Program Template",
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
    },
    [CompleteTransferParticipantResultFormKey.clientName]: {
      id: CompleteTransferParticipantResultFormKey.clientName,
      label: getResultLabel(bulkTransferSiteCoreKeys.resultClient),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
    },
    [CompleteTransferParticipantResultFormKey.nextViewName]: {
      id: CompleteTransferParticipantResultFormKey.nextViewName,
      label: getResultLabel(bulkTransferSiteCoreKeys.resultNextView),
      className: "cell",
      isSortVisible: true,
      isEmptyValue: true,
    },
  };

  const formHeaderMap = {
    [CompleteTransferParticipantResultFormKey.participantName]: (
      <FormController
        key={CompleteTransferParticipantResultFormKey.participantName}
        control={control}
        name={CompleteTransferParticipantResultFormKey.participantName}
      >
        <MuiTextFieldWithSearchIcon
          id={`${pageId}-${CompleteTransferParticipantResultFormKey.participantName}`}
          placeholder={commonLabels.search}
          showCloseIcon
          maxLength={MAX_LENGTH_75}
          shouldTrimSpaces={false}
          requiredARIALabel={commonARIALabel.required}
          searchARIALabel={commonARIALabel.search}
        />
      </FormController>
    ),
    [CompleteTransferParticipantResultFormKey.sourceCountryName]: (
      <FormController
        key={CompleteTransferParticipantResultFormKey.sourceCountryName}
        control={control}
        name={CompleteTransferParticipantResultFormKey.sourceCountryName}
      >
        <MuiSelect
          label=""
          visibleDefaultSelectOption
          options={uniqueSourceCountry}
          value=""
          id={`${pageId}-${CompleteTransferParticipantResultFormKey.sourceCountryName}`}
          placeholder={commonLabels.selectPlaceHolder}
          requiredARIALabel={commonARIALabel.required}
          showSearch
          searchPlaceholder={commonLabels.search}
        />
      </FormController>
    ),
    [CompleteTransferParticipantResultFormKey.destinationCountryName]: (
      <FormController
        key={CompleteTransferParticipantResultFormKey.destinationCountryName}
        control={control}
        name={CompleteTransferParticipantResultFormKey.destinationCountryName}
      >
        <MuiSelect
          label=""
          visibleDefaultSelectOption
          options={uniqueDestinationCountry}
          value=""
          id={`${pageId}-${CompleteTransferParticipantResultFormKey.destinationCountryName}`}
          placeholder={commonLabels.selectPlaceHolder}
          requiredARIALabel={commonARIALabel.required}
          showSearch
          searchPlaceholder={commonLabels.search}
        />
      </FormController>
    ),
    [CompleteTransferParticipantResultFormKey.productLine]: (
      <FormController
        key={CompleteTransferParticipantResultFormKey.productLine}
        control={control}
        name={CompleteTransferParticipantResultFormKey.productLine}
      >
        <MuiSelect
          label=""
          visibleDefaultSelectOption
          options={uniqueLineOfBusinesses}
          value=""
          id={`${pageId}-${CompleteTransferParticipantResultFormKey.productLine}`}
          placeholder={commonLabels.selectPlaceHolder}
          requiredARIALabel={commonARIALabel.required}
        />
      </FormController>
    ),
    [CompleteTransferParticipantResultFormKey.sourceProgramTemplate]: (
      <FormController
        key={CompleteTransferParticipantResultFormKey.sourceProgramTemplate}
        control={control}
        name={CompleteTransferParticipantResultFormKey.sourceProgramTemplate}
      >
        <MuiSelect
          label=""
          visibleDefaultSelectOption
          options={uniqueSourceProgramTemplates}
          value=""
          id={`${pageId}-${CompleteTransferParticipantResultFormKey.sourceProgramTemplate}`}
          placeholder={commonLabels.selectPlaceHolder}
          requiredARIALabel={commonARIALabel.required}
        />
      </FormController>
    ),
    [CompleteTransferParticipantResultFormKey.compatibleProgramTemplate]: (
      <FormController
        key={CompleteTransferParticipantResultFormKey.compatibleProgramTemplate}
        control={control}
        name={
          CompleteTransferParticipantResultFormKey.compatibleProgramTemplate
        }
      >
        <MuiSelect
          label=""
          visibleDefaultSelectOption
          options={uniqueCompatibleProgramTemplates}
          value=""
          id={`${pageId}-${CompleteTransferParticipantResultFormKey.compatibleProgramTemplate}`}
          placeholder={commonLabels.selectPlaceHolder}
          requiredARIALabel={commonARIALabel.required}
        />
      </FormController>
    ),
    [CompleteTransferParticipantResultFormKey.clientName]: (
      <FormController
        key={CompleteTransferParticipantResultFormKey.clientName}
        control={control}
        name={CompleteTransferParticipantResultFormKey.clientName}
      >
        <MuiTextFieldWithSearchIcon
          id={`${pageId}-${CompleteTransferParticipantResultFormKey.clientName}`}
          placeholder={commonLabels.search}
          showCloseIcon
          maxLength={MAX_LENGTH_75}
          shouldTrimSpaces={false}
          requiredARIALabel={commonARIALabel.required}
          searchARIALabel={commonARIALabel.search}
        />
      </FormController>
    ),
    [CompleteTransferParticipantResultFormKey.nextViewName]: (
      <FormController
        key={CompleteTransferParticipantResultFormKey.nextViewName}
        control={control}
        name={CompleteTransferParticipantResultFormKey.nextViewName}
      >
        <MuiSelect
          label=""
          visibleDefaultSelectOption
          options={uniqueNextView}
          value=""
          id={`${pageId}-${CompleteTransferParticipantResultFormKey.nextViewName}`}
          placeholder={commonLabels.selectPlaceHolder}
          requiredARIALabel={commonARIALabel.required}
        />
      </FormController>
    ),
  };
  const noteColumn = {
    id: CompleteTransferParticipantResultFormKey.notes,
    label: getTransferLabel(bulkTransferSiteCoreKeys.failureReason),
    className: "cell",
    isSortVisible: false,
    isEmptyValue: false,
    render: (row: any) => (
      <MuiBox>
        <MuiNotesToolTip message={row?.failureReason || "-"} />
      </MuiBox>
    ),
  };

  const sequence =
    transferType === transferTypeKey.changeCountry
      ? countryTransferSequence
      : programTransferSequence;

  const getColumns = (isUnprocessed: boolean) => {
    const baseColumns = sequence.map((key) => fullColumnMap[key]);

    if (!isUnprocessed) return baseColumns;

    // Insert noteColumn at index 2 if unprocessed
    return [...baseColumns.slice(0, 1), noteColumn, ...baseColumns.slice(1)];
  };
  const getTableHeaderForm = (isUnprocessed: boolean) => {
    const baseHeaders = sequence.map((key) => formHeaderMap[key]);

    if (!isUnprocessed) return baseHeaders;

    return [
      ...baseHeaders.slice(0, 1),
      <></>, // Insert null for the Notes column (no filter needed)
      ...baseHeaders.slice(1),
    ];
  };

  return (
    <SearchParticipantsResultWrapper>
      <MuiTable
        ref={tableRef}
        pageId={pageId}
        tableHeaderForm={getTableHeaderForm(isUnprocessed ?? false)}
        columns={getColumns(isUnprocessed ?? false)}
        tableResults={[...filteredResults]}
        noDataInApi={filteredResults.length === 0}
        noResultMessage={commonLabels.noResult}
        priorityResults={[]}
        emptyMessageColSpan={8}
      />
    </SearchParticipantsResultWrapper>
  );
};
